import {
  fetchCobrandingImage,
  fetchDealerAppointmentSchedule,
  fetchDealerCobrandingStatus,
  fetchDealerInCobrandingGroupStatus,
  fetchDealerInfo,
  postDealerOffer,
  putDealerAppointmentRequest,
} from '../services/service.dealer';
import EventBus from '../services/service.eventBus';
import { state as cobrandingState } from '../store/store.cobranding';
import { state as dealerState } from '../store/store.dealer';
import { state as dynamicEntryState } from '../store/store.dynamic-entry';
import { state as funnelComponentState } from '../store/store.funnel-component';
import { state as offerState } from '../store/store.offer';
import { state as pageParamsState } from '../store/store.page-params';
import { appointmentToDealer, appointmentsToDealers, dataFixDealerInfo, dataFixDealers, findDealerIndex, getDealerName } from '../utils/utils.dealer';
import { isLucidGroup } from '../utils/utils.dynamic-entry';
import { determineWorkflowForSuperComponent } from '../utils/utils.funnel-component';
import { GUID_EMPTY } from '../utils/utils.interactive-banner';
import { DR_BUTTON_TEXT } from '../utils/utils.offer';

import { setAnswersFromDealer } from './actions.answers';
import { setOfferCertificateDisplayFlags } from './actions.offer';

export const onSendDealerOffer = (dealerId: string, offerId: string) => {
  EventBus.publish('SendDealerOffer', { dealerId, offerId });

  // update the buying center dealer to have sent offer
  const { buyingCenters } = dealerState;
  const updatedDealerIdx = findDealerIndex(buyingCenters, dealerId);

  // update dealer object but maintain index order
  buyingCenters[updatedDealerIdx] = { ...buyingCenters[updatedDealerIdx], isOfferSentToDealer: true };
  // set immutable copy on state
  dealerState.buyingCenters = [...buyingCenters];

  const dealer = dealerState.dealerOffers[dealerId];
  dealer.hasOffer = true;

  dealerState.dealerOffers = { ...dealerState.dealerOffers, ...dealer };

  postDealerOffer(dealerId, offerId)
    .then((response) => onSendDealerOfferSuccess({ dealerId, offerId, response }))
    .catch((error) => onSendDealerOfferFailure(error));
};

export const onSendDealerOfferSuccess = (res): void => {
  EventBus.publish('SendDealerOfferSuccess', res);
};

export const onSendDealerOfferFailure = (error): void => {
  EventBus.publish('onSendDealerOfferFailure', error);
};

export const onOfferLoad = ({ prospect, appointments }) => {
  const { dealershipsAndInventory, dealerId, dealerRepId, cobranding } = prospect;
  const { allocationEntries, buyingCenters, tradeInCenters } = dealershipsAndInventory;
  const buyingCentersWithApptData = appointmentsToDealers(dataFixDealers(buyingCenters), appointments).map((dealer) => ({ ...dealer, isOfferSentToDealer: false }));
  const nonDupDealers = new Map();
  allocationEntries.forEach((item) => {
    const key = item.dealerInfo.dealerId;
    if (nonDupDealers.has(key)) {
      nonDupDealers.get(key).push(item);
    } else {
      nonDupDealers.set(key, [item]);
    }
  });
  const allocatedWithApptData = Array.from(nonDupDealers)
    .map(([, value]) => value[0])
    .map((a) => ({
      dealerInfo: { ...appointmentToDealer(dataFixDealerInfo(a.dealerInfo), appointments), isOfferSentToDealer: true },
      vehicleInfo: a.vehicleInfo,
      dealerInventory: allocationEntries.filter((item) => item.dealerInfo.dealerId === a.dealerInfo.dealerId).slice(0, 3),
    }));

  // Map appointments
  dealerState.dealerOffers = appointments?.reduce((accum, dealer) => {
    const { dealerId = 0, dealerGuid = '', isOfferSentToDealer = false, isRequestedAppointment = false } = dealer;
    accum[dealerId] = { dealerGuid, hasAppointment: isRequestedAppointment, hasOffer: isOfferSentToDealer, appointmentDay: '', appointmentTime: '', schedule: [] };

    return accum;
  }, {});

  // Load co branding info for dealer group
  if (cobranding.groupId) {
    onFetchDealerCobrandingStatus(cobranding.groupId);
  }

  dealerState.allocated = allocatedWithApptData;
  dealerState.buyingCenters = buyingCentersWithApptData;
  dealerState.dealerId = dealerId;
  dealerState.dealerRepId = dealerRepId;
  dealerState.tradeInCenters = tradeInCenters;
  // DAT created offers don't have a dealerId on them by design, so try and find the dealer name on the page props
  if (!dealerId) {
    dealerState.name = pageParamsState.dealerName ? pageParamsState.dealerName : dealerState.name;
  } else {
    dealerState.name = getDealerName(allocatedWithApptData, dealerId);
  }
  dealerState.numAssignableDealers = allocatedWithApptData.length + buyingCentersWithApptData.length;
};

export const onDealerAppointmentRequested = (appointmentData): void => {
  EventBus.publish('AppointmentRequest', appointmentData);

  putDealerAppointmentRequest(appointmentData)
    .then(() => {
      onDealerAppointmentRequestedSuccess(appointmentData);
    })
    .catch((error) => {
      onDealerAppointmentRequestedFailure(error);
    });
};

export const onFetchDealerCobrandingStatus = async (groupId: string): Promise<void> => {
  EventBus.publish('CobrandingRequest', groupId);

  try {
    const res = await fetchDealerCobrandingStatus(groupId);
    onFetchDealerCobrandingStatusSuccess(res);
  } catch (error) {
    onFetchDealerCobrandingStatusFailure(error);
  }
};

export const onFetchDealerCobrandingStatusByDealerId = async (dealerGroupId: string, cobrandingGroupId: string, dealerId: string): Promise<void> => {
  EventBus.publish('CobrandingRequest', cobrandingGroupId);

  try {
    const res = await fetchDealerInCobrandingGroupStatus(dealerGroupId, cobrandingGroupId);
    onFetchDealerCobrandingStatusByDealerIdSuccess(res);
    if (!dealerId) {
      onFetchDealerInfo(res.data.dealerId);
    }
  } catch (error) {
    onFetchDealerCobrandingStatusFailure(error);
  }
};

export const onFetchDealerCobrandingStatusSuccess = (cobrandingData): void => {
  const {
    enabled,
    groupId,
    isDigitalRetailer,
    isRequirePhoto,
    acceptsReplacementVehicleAnswers,
    replacementVehicleMakes,
    replacementVehicleModels,
    replacementVehicleTypes,
    internalGroupID,
    isRequireVinPlate,
    measurementID,
    allowIneligibleVehicle,
    isSuperComponent,
    isOEMAppraisal,
    isOEMTiC,
  } = cobrandingData.data;

  const superComponentWorkflow = determineWorkflowForSuperComponent(cobrandingState);

  dealerState.isDealerCobrandingDrEnabled = enabled && isDigitalRetailer;
  cobrandingState.isDigitalRetailer = isDigitalRetailer;
  cobrandingState.isRequirePhoto = isRequirePhoto;
  cobrandingState.isRequireVinPlate = isRequireVinPlate;
  cobrandingState.allowIneligibleVehicle = allowIneligibleVehicle;
  cobrandingState.acceptsReplacementVehicleAnswers = acceptsReplacementVehicleAnswers;
  cobrandingState.replacementVehicleMakes = replacementVehicleMakes;
  cobrandingState.replacementVehicleModels = replacementVehicleModels;
  cobrandingState.replacementVehicleTypes = replacementVehicleTypes;
  cobrandingState.cobrandingGroupId = groupId;
  cobrandingState.internalGroupID = internalGroupID;
  cobrandingState.enabled = enabled;
  cobrandingState.cobrandingMeasurementId = measurementID;
  cobrandingState.isSuperComponent = isSuperComponent;
  cobrandingState.isOEMAppraisal = isOEMAppraisal;
  cobrandingState.isOEMTiC = isOEMTiC;
  dynamicEntryState.dynamicEntryWorkflow = funnelComponentState.isSuperComponent && !isLucidGroup(groupId) ? superComponentWorkflow : dynamicEntryState.dynamicEntryWorkflow;
  offerState.inProgressBtnText = funnelComponentState.isSuperComponent && dealerState.isDealerCobrandingDrEnabled ? DR_BUTTON_TEXT.CONTINUE : '';
  onFetchCobrandingImage(cobrandingState.internalGroupID);

  setOfferCertificateDisplayFlags();

  EventBus.publish('CobrandingRequestSuccess', groupId);
};

export const onFetchDealerInfo = (dealerId: string): void => {
  EventBus.publish('FetchDealerInfoRequest', dealerId);

  fetchDealerInfo(dealerId)
    .then((res) => {
      onFetchDealerInfoSuccess(res);
    })
    .catch((error) => {
      onFetchDealerInfoFailure(error);
    });
};

export const onFetchDealerInfoSuccess = (res): void => {
  dealerState.acceptsReplacementVehicleAnswers = res.data.acceptsReplacementVehicleAnswers;
  dealerState.dealerGuid = res.data.dealerGuid;
  dealerState.name = res.data.displayName;
  dealerState.enabled = res.data.dealerGuid && res.data.dealerGuid !== GUID_EMPTY;
  dealerState.measurementId = res?.data?.measurementId;
  EventBus.publish('FetchDealerInfoRequestSuccess', res);
};

export const onFetchDealerInfoFailure = (error): void => {
  dealerState.enabled = false;
  EventBus.publish('FetchDealerInfoRequestError', error);
};

export const onFetchDealerCobrandingStatusByDealerIdSuccess = (cobrandingData): void => {
  const {
    enabled,
    groupId,
    isDigitalRetailer,
    isRequirePhoto,
    acceptsReplacementVehicleAnswers,
    replacementVehicleMakes,
    replacementVehicleModels,
    replacementVehicleTypes,
    internalGroupID,
    isRequireVinPlate,
    allowIneligibleVehicle,
    measurementID,
    isSuperComponent,
    isOEMAppraisal,
    isOEMTiC,
  } = cobrandingData.data.cobrandingGroup;

  const superComponentWorkflow = determineWorkflowForSuperComponent(cobrandingState);

  cobrandingState.dealerId = cobrandingData.data.dealerId;
  cobrandingState.dealerGuid = cobrandingData.data.dealerGuid;
  cobrandingState.dealerName = cobrandingData.data.displayName;
  cobrandingState.isDigitalRetailer = isDigitalRetailer;
  dealerState.isDealerCobrandingDrEnabled = enabled && isDigitalRetailer;
  cobrandingState.isRequirePhoto = isRequirePhoto;
  cobrandingState.isRequireVinPlate = isRequireVinPlate;
  cobrandingState.allowIneligibleVehicle = allowIneligibleVehicle;
  cobrandingState.acceptsReplacementVehicleAnswers = acceptsReplacementVehicleAnswers;
  cobrandingState.replacementVehicleMakes = replacementVehicleMakes;
  cobrandingState.replacementVehicleModels = replacementVehicleModels;
  cobrandingState.replacementVehicleTypes = replacementVehicleTypes;
  cobrandingState.cobrandingGroupId = groupId;
  cobrandingState.internalGroupID = internalGroupID;
  cobrandingState.enabled = enabled;
  cobrandingState.cobrandingMeasurementId = measurementID;
  cobrandingState.isSuperComponent = isSuperComponent;
  cobrandingState.isOEMAppraisal = isOEMAppraisal;
  cobrandingState.isOEMTiC = isOEMTiC;
  dynamicEntryState.dynamicEntryWorkflow = funnelComponentState.isSuperComponent && !isLucidGroup(groupId) ? superComponentWorkflow : dynamicEntryState.dynamicEntryWorkflow;
  offerState.inProgressBtnText = funnelComponentState.isSuperComponent && dealerState.isDealerCobrandingDrEnabled ? DR_BUTTON_TEXT.CONTINUE : '';
  onFetchCobrandingImage(cobrandingState.internalGroupID);
  setOfferCertificateDisplayFlags();

  EventBus.publish('CobrandingRequestSuccess', groupId);
};

export const onFetchDealerCobrandingStatusFailure = (error): void => {
  cobrandingState.enabled = false;
  cobrandingState.imageUrl = 'assets/main-image-kbb.svg';
  EventBus.publish('CobrandingStatus', error);
};
export const onAppointmentRequestSelected = (appointmentData): void => {
  EventBus.publish('AppointmentRequestSelect', appointmentData);
};

export const onDealerAppointmentRequestedSuccess = (appointmentData): void => {
  const { day, dealerId, month, timeFrame, year } = appointmentData;
  const { allocated, buyingCenters } = dealerState;
  const allocatedDealerIdx = findDealerIndex(dealerState.allocated, dealerId);
  const buyingCenterDealerIdx = findDealerIndex(dealerState.buyingCenters, dealerId);

  // Add appointment time to whichever dealer it was allocated || buying center.
  if (allocatedDealerIdx > -1) {
    allocated[allocatedDealerIdx].dealerInfo = {
      ...allocated[allocatedDealerIdx].dealerInfo,
      appointmentDay: day,
      appointmentMonth: month,
      appointmentYear: year,
      appointmentTime: timeFrame,
      hasAppointment: true,
    };

    dealerState.allocated = [...dealerState.allocated];
  } else {
    buyingCenters[buyingCenterDealerIdx] = {
      ...buyingCenters[buyingCenterDealerIdx],
      appointmentDay: day,
      appointmentMonth: month,
      appointmentYear: year,
      appointmentTime: timeFrame,
      hasAppointment: true,
    };

    dealerState.buyingCenters = [...buyingCenters];
  }

  dealerState.dealerAppointmentInfo = {
    appointmentDay: day,
    appointmentMonth: month,
    appointmentYear: year,
    appointmentTime: timeFrame,
    dealershipName: [...dealerState.allocated.map((dealer) => dealer.dealerInfo), ...dealerState.buyingCenters].find((dealer) => dealer.dealerId === dealerId).dealershipName,
    dealerId,
  };

  EventBus.publish('AppointRequestSuccess', appointmentData);
};

export const onDealerAppointmentRequestedFailure = (error): void => {
  EventBus.publish('AppointmentRequestFail', error);
};

export const onDealerScheduleRequested = (dealerId) => {
  EventBus.publish('DealerScheduleRequested', dealerId);

  onDealerScheduleFetch(dealerId);
};

export const onDealerScheduleFetch = (dealerId: string): void => {
  fetchDealerAppointmentSchedule(dealerId)
    .then((res) => onDealerAppointmentScheduleFetchSuccess(res.data))
    .catch((error) => {
      onAppointmentScheduleFetchFailure(error);
    });
};

export const onDealerAppointmentScheduleFetchSuccess = ({ dealerId, settings }): void => {
  const schedule = settings
    .split(',')
    .map((dayTimeString) => dayTimeString.split('-'))
    .map(([day, startTime, endTime]) => ({ day, startTime, endTime }));

  // update dealer with schedule
  const dealer = dealerState.dealerOffers[dealerId];
  dealer.schedule = schedule;

  // return new copy
  dealerState.dealerOffers = { ...dealerState.dealerOffers, ...dealer };

  EventBus.publish('DealerAppointmentScheduleFetchSuccess', { dealerId, schedule, settings });
};

export const onAppointmentScheduleFetchFailure = (error): void => {
  EventBus.publish('AppointmentScheduleFetchFailure', error);
  console.log(error);
};

export const onVehiclesDealerInventory = (dealerInventory) => {
  return dealerInventory && dealerInventory.filter((item) => item.vehicleInfo.vehicleId !== '');
};

export const onReceiveDealerRepInfo = (dealerRepId, dealerRepName, inviteId, authToken) => {
  setAnswersFromDealer({ dealerRepId, dealerRepName, inviteId, authToken });
};

export const onFetchCobrandingImage = (internalGroupID: number): void => {
  const grouKey = `cobranding_${internalGroupID}`;
  const itemKey = 'cobranding_landing_page.png';
  EventBus.publish('FetchCobrandingImageRequest', `${grouKey}/${itemKey}`);

  fetchCobrandingImage(grouKey, itemKey)
    .then((res) => {
      onFetchCobrandingImageSuccess(res);
    })
    .catch((error) => {
      onFetchCobrandingImageFailure(error);
    });
};

const onFetchCobrandingImageSuccess = (cobrandingImage): void => {
  if (!cobrandingImage.data.items[0].getContentsUrl) {
    cobrandingState.imageUrl = 'assets/main-image-kbb.svg';
  } else {
    cobrandingState.imageUrl = `${cobrandingImage.data.items[0].getContentsUrl}?${Date.now()}`;
  }

  EventBus.publish('onFetchCobrandingImageSuccess', cobrandingImage);
};

const onFetchCobrandingImageFailure = (error): void => {
  cobrandingState.imageUrl = 'assets/main-image-kbb.svg';
  EventBus.publish('onFetchCobrandingImageFailure', error);
  console.log(error);
};

export default {
  onFetchDealerCobrandingStatus,
  onFetchDealerCobrandingStatusByDealerId,
  onDealerAppointmentRequested,
  onDealerAppointmentRequestedSuccess,
  onDealerAppointmentRequestedFailure,
  onDealerScheduleFetch,
  onDealerScheduleRequested,
  onSendDealerOffer,
  onSendDealerOfferSuccess,
  onSendDealerOfferFailure,
  onOfferLoad,
  onVehiclesDealerInventory,
  onReceiveDealerRepInfo,
  onFetchDealerInfo,
};
